import { useState, useEffect, useCallback } from 'react';
import PropTypes from 'prop-types';
import {
    Dialog,
    DialogContent,
    Box,
    Typography,
    Avatar,
    IconButton,
    Button,
    Grid,
    Paper,
    Checkbox,
    CircularProgress,
    Fade,
    TextField,
    Collapse
} from '@mui/material';
import { Close, Email, LanguageOutlined, CheckCircle } from '@mui/icons-material';
import { motion, AnimatePresence } from 'framer-motion';
import { handleCatchError } from 'utils/CatchHandler';
import { addEmployeePortalStatus, getEmployeePortalList } from 'utils/API/Mis Apis/HrPortelApis';
import { globalNotifySuccess } from 'views/utilities/AlertToast';

const QuickViewModal = ({ open, handleClose, employee, onRefresh }) => {
    const [portals, setPortals] = useState([]);
    const [selectedPortals, setSelectedPortals] = useState({});
    const [isLoadingPortals, setIsLoadingPortals] = useState(false);
    const [isLoading, setIsLoading] = useState(false);
    const [formErrors, setFormErrors] = useState({});
    const roleColor = '#1976d2';

    const fetchPortals = useCallback(async () => {
        setIsLoadingPortals(true);
        try {
            const response = await getEmployeePortalList({ employee_id: employee?.id });
            if (response?.data?.success) {
                const fetchedPortals = response.data.data || [];
                setPortals(fetchedPortals);

                // Pre-select portals if data exists
                const initialSelected = {};
                fetchedPortals.forEach((portal) => {
                    const portalData = portal.employee_portal_data;
                    if (portalData) {
                        const status = portalData.status || 'Active';
                        initialSelected[portal.id] = {
                            username: portalData.username || '',
                            phone: portalData.phone || '',
                            status: status.charAt(0).toUpperCase() + status.slice(1).toLowerCase() // Canonical: Active or Inactive
                        };
                    }
                });
                setSelectedPortals(initialSelected);
            }
        } catch (error) {
            handleCatchError(error);
        } finally {
            setIsLoadingPortals(false);
        }
    }, [employee?.id]);

    const handleEmployeeStatus = async () => {
        setIsLoading(true);
        try {
            const errors = {};
            const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
            const phoneRegex = /^\d{10}$/;

            Object.entries(selectedPortals).forEach(([portalId, data]) => {
                if (data.status?.toLowerCase() === 'active') {
                    const portalErrors = {};
                    if (!data.username || !emailRegex.test(data.username)) {
                        portalErrors.username = 'Enter a valid email address';
                    }
                    if (!data.phone || !phoneRegex.test(data.phone)) {
                        portalErrors.phone = 'Enter a valid 10-digit phone number';
                    }
                    if (Object.keys(portalErrors).length > 0) {
                        errors[portalId] = portalErrors;
                    }
                }
            });

            if (Object.keys(errors).length > 0) {
                setFormErrors(errors);
                return;
            }

            const payload = {
                employee_id: employee.id,
                services: Object.entries(selectedPortals).map(([portalId, data]) => {
                    const portalObj = portals.find((p) => p.id === Number(portalId));
                    return {
                        portal_service_id: Number(portalId),
                        portal_service_name: portalObj ? portalObj.portal_name : '',
                        username: data.username,
                        phone: data.phone,
                        status: data.status
                    };
                })
            };
            const response = await addEmployeePortalStatus(payload);
            if (response?.data?.success) {
                globalNotifySuccess(response.data.message || 'Portal status updated successfully');
                if (onRefresh) onRefresh();
                handleClose();
            }
        } catch (error) {
            handleCatchError(error);
        } finally {
            setIsLoading(false);
        }
    };

    useEffect(() => {
        if (open) {
            setSelectedPortals({});
            setFormErrors({});
            fetchPortals();
        }
    }, [open, fetchPortals]);

    const handleTogglePortal = (id) => {
        setSelectedPortals((prev) => {
            const next = { ...prev };
            const portalObj = portals.find((p) => p.id === Number(id));
            const isOriginallyAssigned = portalObj && portalObj.employee_portal_data;

            if (next[id]) {
                if (isOriginallyAssigned) {
                    const currentStatus = next[id].status?.toLowerCase() || 'active';
                    next[id] = {
                        ...next[id],
                        status: currentStatus === 'active' ? 'Inactive' : 'Active'
                    };
                } else {
                    delete next[id];
                }
            } else {
                next[id] = {
                    username: employee.email || '',
                    phone: employee.phone || '',
                    status: 'Active'
                };
            }

            setFormErrors((prev) => {
                const newErrors = { ...prev };
                delete newErrors[id];
                return newErrors;
            });

            return next;
        });
    };

    const handleUsernameChange = (id, val) => {
        setSelectedPortals((prev) => ({
            ...prev,
            [id]: { ...prev[id], username: val }
        }));
        setFormErrors((prev) => {
            if (!prev[id]?.username) return prev;
            const next = { ...prev };
            delete next[id].username;
            if (Object.keys(next[id]).length === 0) delete next[id];
            return next;
        });
    };

    const handlePhoneChange = (id, val) => {
        const numericVal = val.replace(/\D/g, '').slice(0, 10);
        setSelectedPortals((prev) => ({
            ...prev,
            [id]: { ...prev[id], phone: numericVal }
        }));
        setFormErrors((prev) => {
            if (!prev[id]?.phone) return prev;
            const next = { ...prev };
            delete next[id].phone;
            if (Object.keys(next[id]).length === 0) delete next[id];
            return next;
        });
    };

    if (!employee) return null;

    return (
        <Dialog
            open={open}
            onClose={handleClose}
            fullWidth
            maxWidth="xs"
            PaperProps={{
                sx: {
                    borderRadius: 4,
                    overflow: 'hidden',
                    bgcolor: 'rgba(255, 255, 255, 0.9)',
                    backdropFilter: 'blur(20px)',
                    border: '1px solid rgba(255, 255, 255, 0.5)',
                    boxShadow: '0 24px 48px rgba(0,0,0,0.15)',
                    display: 'flex',
                    flexDirection: 'column',
                    maxHeight: '90vh'
                }
            }}
            slotProps={{
                backdrop: {
                    sx: {
                        backgroundColor: 'rgba(0, 0, 0, 0.2)',
                        backdropFilter: 'blur(4px)'
                    }
                }
            }}
        >
            <AnimatePresence>
                {open && (
                    <motion.div
                        initial={{ opacity: 0, y: 10 }}
                        animate={{ opacity: 1, y: 0 }}
                        exit={{ opacity: 0, y: 10 }}
                        transition={{ duration: 0.3 }}
                        style={{ display: 'flex', flexDirection: 'column', flex: 1, minHeight: 0, overflow: 'hidden' }}
                    >
                        <Box
                            sx={{
                                background: 'linear-gradient(135deg, #1e3a8a 0%, #1976d2 60%, #42a5f5 100%)',
                                px: 3,
                                py: 2.5,
                                position: 'relative',
                                color: '#fff',
                                overflow: 'hidden'
                            }}
                        >
                            {[
                                { size: 100, top: -40, right: -20, op: 0.1 },
                                { size: 60, top: 20, right: 60, op: 0.05 }
                            ].map((c, i) => (
                                <Box
                                    key={i}
                                    sx={{
                                        position: 'absolute',
                                        top: c.top,
                                        right: c.right,
                                        width: c.size,
                                        height: c.size,
                                        borderRadius: '50%',
                                        bgcolor: '#fff',
                                        opacity: c.op,
                                        pointerEvents: 'none'
                                    }}
                                />
                            ))}

                            <IconButton
                                onClick={handleClose}
                                size="small"
                                sx={{
                                    position: 'absolute',
                                    top: 12,
                                    right: 12,
                                    color: 'rgba(255,255,255,0.7)',
                                    zIndex: 2,
                                    '&:hover': { color: '#fff', bgcolor: 'rgba(255,255,255,0.1)' }
                                }}
                            >
                                <Close fontSize="small" />
                            </IconButton>

                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                                <Avatar
                                    sx={{
                                        width: 56,
                                        height: 56,
                                        fontSize: 24,
                                        fontWeight: 800,
                                        bgcolor: '#fff',
                                        color: roleColor,
                                        boxShadow: '0 4px 12px rgba(0,0,0,0.1)',
                                        border: '2px solid rgba(255,255,255,0.2)'
                                    }}
                                >
                                    {employee.name.charAt(0)}
                                </Avatar>
                                <Box>
                                    <Typography variant="h5" fontWeight={800} sx={{ letterSpacing: '-0.01em', color: '#fff' }}>
                                        {employee.name}
                                    </Typography>
                                    <Typography
                                        variant="caption"
                                        sx={{
                                            opacity: 0.85,
                                            fontWeight: 600,
                                            textTransform: 'uppercase',
                                            letterSpacing: '0.05em',
                                            fontSize: '0.65rem',
                                            color: '#fff'
                                        }}
                                    >
                                        {employee.role?.replace('_', ' ')}
                                    </Typography>
                                </Box>
                            </Box>
                        </Box>

                        <DialogContent sx={{ p: 3 }}>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 3, px: 1 }}>
                                <Box sx={{ p: 1, borderRadius: 2, bgcolor: 'primary.50', color: 'primary.main', display: 'flex' }}>
                                    <Email fontSize="small" />
                                </Box>
                                <Box>
                                    <Typography
                                        variant="caption"
                                        color="text.secondary"
                                        fontWeight={700}
                                        sx={{ fontSize: '0.6rem', textTransform: 'uppercase' }}
                                    >
                                        Email Address
                                    </Typography>
                                    <Typography variant="body2" fontWeight={700} color="text.primary">
                                        {employee.email || 'N/A'}
                                    </Typography>
                                </Box>
                            </Box>

                            <Box>
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2, px: 1 }}>
                                    <LanguageOutlined sx={{ fontSize: 18, color: 'text.secondary' }} />
                                    <Typography variant="subtitle2" fontWeight={800} color="text.primary">
                                        Assign Portals
                                    </Typography>
                                </Box>

                                {isLoadingPortals ? (
                                    <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
                                        <CircularProgress size={24} thickness={5} />
                                    </Box>
                                ) : (
                                    <Grid container spacing={1.5}>
                                        {portals.map((portal) => {
                                            const portalData = selectedPortals[portal.id];
                                            const isSelected = portalData && portalData.status?.toLowerCase() === 'active';
                                            return (
                                                <Grid item xs={12} key={portal.id}>
                                                    <Paper
                                                        elevation={0}
                                                        sx={{
                                                            p: 1.5,
                                                            borderRadius: 3,
                                                            border: '2px solid',
                                                            borderColor: isSelected ? 'primary.main' : 'divider',
                                                            bgcolor: isSelected ? 'primary.50' : '#fff',
                                                            transition: 'all 0.2s cubic-bezier(0.4, 0, 0.2, 1)',
                                                            position: 'relative',
                                                            overflow: 'hidden',
                                                            '&:hover': {
                                                                borderColor: isSelected ? 'primary.main' : 'primary.light',
                                                                bgcolor: isSelected ? 'primary.50' : 'rgba(25, 118, 210, 0.04)'
                                                            }
                                                        }}
                                                    >
                                                        <Box
                                                            onClick={() => handleTogglePortal(portal.id)}
                                                            sx={{
                                                                display: 'flex',
                                                                alignItems: 'center',
                                                                gap: 1,
                                                                cursor: 'pointer',
                                                                mb: isSelected ? 1.5 : 0
                                                            }}
                                                        >
                                                            <Checkbox
                                                                size="small"
                                                                checked={isSelected}
                                                                sx={{
                                                                    p: 0.5,
                                                                    color: 'divider',
                                                                    '&.Mui-checked': { color: 'primary.main' }
                                                                }}
                                                            />
                                                            <Typography
                                                                variant="body2"
                                                                fontWeight={isSelected ? 800 : 600}
                                                                sx={{
                                                                    fontSize: '0.75rem',
                                                                    color: isSelected ? 'primary.main' : 'text.primary',
                                                                    transition: 'color 0.2s'
                                                                }}
                                                            >
                                                                {portal.portal_name}
                                                            </Typography>
                                                        </Box>

                                                        <Collapse in={isSelected}>
                                                            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1, mt: 1 }}>
                                                                <TextField
                                                                    fullWidth
                                                                    size="small"
                                                                    placeholder="Enter your email"
                                                                    value={selectedPortals[portal.id]?.username || ''}
                                                                    onChange={(e) => handleUsernameChange(portal.id, e.target.value)}
                                                                    onClick={(e) => e.stopPropagation()}
                                                                    error={!!formErrors[portal.id]?.username}
                                                                    helperText={formErrors[portal.id]?.username}
                                                                    InputProps={{
                                                                        sx: {
                                                                            fontSize: '0.75rem',
                                                                            borderRadius: 2,
                                                                            bgcolor: '#fff',
                                                                            '& fieldset': {
                                                                                borderColor: formErrors[portal.id]?.username
                                                                                    ? 'error.main'
                                                                                    : 'primary.light'
                                                                            }
                                                                        }
                                                                    }}
                                                                />
                                                                <TextField
                                                                    fullWidth
                                                                    size="small"
                                                                    placeholder="Enter mobile number"
                                                                    value={selectedPortals[portal.id]?.phone || ''}
                                                                    onChange={(e) => handlePhoneChange(portal.id, e.target.value)}
                                                                    onClick={(e) => e.stopPropagation()}
                                                                    error={!!formErrors[portal.id]?.phone}
                                                                    helperText={formErrors[portal.id]?.phone}
                                                                    InputProps={{
                                                                        sx: {
                                                                            fontSize: '0.75rem',
                                                                            borderRadius: 2,
                                                                            bgcolor: '#fff',
                                                                            '& fieldset': {
                                                                                borderColor: formErrors[portal.id]?.phone
                                                                                    ? 'error.main'
                                                                                    : 'primary.light'
                                                                            }
                                                                        }
                                                                    }}
                                                                />
                                                            </Box>
                                                        </Collapse>

                                                        {isSelected && (
                                                            <Fade in={true}>
                                                                <CheckCircle
                                                                    sx={{
                                                                        position: 'absolute',
                                                                        top: 6,
                                                                        right: 6,
                                                                        fontSize: 18,
                                                                        color: 'primary.main',
                                                                        bgcolor: '#fff',
                                                                        borderRadius: '50%'
                                                                    }}
                                                                />
                                                            </Fade>
                                                        )}
                                                    </Paper>
                                                </Grid>
                                            );
                                        })}
                                    </Grid>
                                )}
                            </Box>

                            <Button
                                fullWidth
                                variant="contained"
                                onClick={handleEmployeeStatus}
                                disabled={isLoading}
                                sx={{
                                    mt: 4,
                                    borderRadius: 3,
                                    py: 1.2,
                                    fontWeight: 800,
                                    textTransform: 'none',
                                    bgcolor: '#1976d2',
                                    boxShadow: '0 8px 16px rgba(25, 118, 210, 0.25)',
                                    '&:hover': {
                                        bgcolor: '#1565c0',
                                        transform: 'translateY(-2px)',
                                        boxShadow: '0 12px 20px rgba(25, 118, 210, 0.35)'
                                    },
                                    transition: 'all 0.2s'
                                }}
                            >
                                Confirm Selections
                            </Button>
                        </DialogContent>
                    </motion.div>
                )}
            </AnimatePresence>
        </Dialog>
    );
};

QuickViewModal.propTypes = {
    open: PropTypes.bool.isRequired,
    handleClose: PropTypes.func.isRequired,
    employee: PropTypes.object,
    onRefresh: PropTypes.func
};

export default QuickViewModal;
